/**
 * @file MapPane.jsx
 * @description Mapbox GL harita bileşeni
 *
 * İlan konumlarını gösteren interaktif harita. Marker'lar,
 * popup'lar, viewport araması ve responsive layout desteği.
 * useMapInstance ve useMapMarkers hook'larını kullanır.
 *
 * @module widget/components/MapPane
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/} Mapbox GL JS
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Map as MapIcon, Search } from "lucide-react";
import { computeValidCoords, fitMapToMarkers } from "../utils";
import { useMapInstance, useMapMarkers } from "../hooks";

/**
 * Harita Paneli Bileşeni
 *
 * Mapbox GL JS kullanarak ilanları harita üzerinde gösterir.
 * İki layout modu destekler: inline (sabit yükseklik) ve
 * fullHeight (tam ekran).
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object[]} props.listings - İlan listesi (koordinat içermeli)
 * @param {boolean} props.isDark - Karanlık mod (harita stili etkiler)
 * @param {Function} [props.onSelectListing] - Marker tıklama callback'i
 * @param {Function} [props.onMapMove] - Harita hareket callback'i
 * @param {Function} [props.onBoundsChange] - Viewport değişim callback'i
 * @param {Function} [props.onSearchInArea] - "Bu alanda ara" callback'i
 * @param {boolean} [props.fullHeight=false] - Tam yükseklik modu
 * @param {number|null} [props.selectedListingId=null] - Seçili ilan ID'si
 * @param {boolean} [props.hideHeader=false] - Başlığı gizle
 * @param {boolean} [props.showSearchButton=false] - Arama butonu göster
 *
 * @returns {JSX.Element} Harita paneli bileşeni
 *
 * @example
 * <MapPane
 *   listings={listings}
 *   isDark={false}
 *   fullHeight={true}
 *   showSearchButton={true}
 *   onSelectListing={(id) => setSelected(id)}
 *   onSearchInArea={(bounds) => searchInBounds(bounds)}
 * />
 *
 * @remarks
 * - Harita stili tema'ya göre otomatik değişir (light/dark)
 * - Marker'lar seçim durumuna göre highlight olur
 * - Viewport araması için bounds bilgisi parent'a iletilir
 */
export function MapPane({
  listings,
  isDark,
  onSelectListing,
  onMapMove,
  onBoundsChange,
  onSearchInArea,
  fullHeight = false,
  selectedListingId = null,
  hideHeader = false,
  showSearchButton = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [hasMoved, setHasMoved] = useState(false);
  const [currentBounds, setCurrentBounds] = useState(null);
  const listingsRef = useRef(listings);

  // Keep listings ref updated for resize effects
  useEffect(() => {
    listingsRef.current = listings;
  }, [listings]);

  // Handle bounds change from map - update local state and notify parent
  const handleBoundsChange = useCallback((bounds) => {
    setCurrentBounds(bounds);
    setHasMoved(true);
    onBoundsChange?.(bounds);
  }, [onBoundsChange]);

  // Initialize map using shared hook
  const { map, mapReady } = useMapInstance({
    mapRef,
    listings,
    isDark,
    onMapMove,
    onBoundsChange: handleBoundsChange,
  });

  // Handle search in area button click
  const handleSearchInArea = useCallback(() => {
    if (currentBounds && onSearchInArea) {
      onSearchInArea(currentBounds);
      setHasMoved(false);
    }
  }, [currentBounds, onSearchInArea]);

  // Use markers hook
  useMapMarkers({
    map,
    mapReady,
    listings,
    selectedListingId,
    onSelectListing,
    containerRef,
  });

  // Resize map when detail panel opens/closes
  useEffect(() => {
    if (!map || !mapReady) return;
    const timer = setTimeout(() => map.resize(), 200);
    return () => clearTimeout(timer);
  }, [map, selectedListingId, mapReady]);

  // Fit map when entering fullHeight mode
  useEffect(() => {
    if (!map || !mapReady || !fullHeight) return;

    const timers = [50, 150, 400].map((delay) =>
      setTimeout(() => {
        map.resize();
        const currentCoords = computeValidCoords(listingsRef.current);
        if (currentCoords.length > 0) {
          fitMapToMarkers(map, currentCoords, false);
        }
      }, delay)
    );

    return () => timers.forEach((t) => clearTimeout(t));
  }, [map, fullHeight, mapReady]);

  // Fit when listings change in fullHeight mode
  useEffect(() => {
    if (!map || !mapReady || !fullHeight) return;

    const currentCoords = computeValidCoords(listings);
    if (currentCoords.length > 0) {
      map.resize();
      fitMapToMarkers(map, currentCoords, false);
    }
  }, [map, listings, fullHeight, mapReady]);

  // Loading overlay
  const loadingOverlay = !mapReady && (
    <div className={`absolute inset-0 flex items-center justify-center ${isDark ? "bg-zinc-800" : "bg-white"}`}>
      <Loader2 className="h-6 w-6 animate-spin opacity-50" />
    </div>
  );

  // Search in area button - Emlakjet green for visibility on both light/dark maps
  // Uses safe-area-inset-top to avoid iPhone notch/Dynamic Island
  const searchButton = showSearchButton && hasMoved && currentBounds && (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-10"
      style={{ top: "calc(12px + env(safe-area-inset-top, 0px))" }}
    >
      <button
        onClick={handleSearchInArea}
        className="search-area-btn flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium shadow-lg"
      >
        <Search className="h-4 w-4" />
        Bu bölgede ara
      </button>
    </div>
  );

  // Header
  const header = !hideHeader && (
    <div
      className={`p-3 border-b text-xs flex items-center gap-2 flex-shrink-0 ${
        isDark ? "border-white/10 text-white/60" : "border-black/10 text-black/60"
      }`}
    >
      <MapIcon className="h-4 w-4" strokeWidth={1.5} />
      Harita (yakınlaştırıp ilan seçebilirsiniz)
    </div>
  );

  // Fullscreen layout
  if (fullHeight) {
    return (
      <div ref={containerRef} className={`h-full w-full flex flex-col ${isDark ? "bg-zinc-800" : "bg-white"}`}>
        {header}
        <div className="flex-1 w-full relative min-h-0">
          <div ref={mapRef} className="absolute inset-0" style={{ width: "100%", height: "100%" }} />
          {loadingOverlay}
          {searchButton}
        </div>
      </div>
    );
  }

  // Inline layout
  return (
    <div
      ref={containerRef}
      className={`rounded-2xl border overflow-hidden shadow-sm ${
        isDark ? "border-white/10 bg-zinc-800" : "border-black/10 bg-white"
      }`}
    >
      <div
        className={`p-3 border-b text-xs flex items-center gap-2 ${
          isDark ? "border-white/5 text-white/60" : "border-black/5 text-black/60"
        }`}
      >
        <MapIcon className="h-4 w-4" strokeWidth={1.5} />
        Harita (yakınlaştırıp ilan seçebilirsiniz)
      </div>
      <div className="h-[400px] sm:h-[500px] w-full relative">
        <div ref={mapRef} className="absolute inset-0" style={{ width: "100%", height: "100%" }} />
        {loadingOverlay}
        {searchButton}
      </div>
    </div>
  );
}

export default MapPane;
